import * as React from "react";
import { CalendarCheck2, Check, DownloadCloud, Unplug, X } from "lucide-react";

import {
  disconnectCalendar,
  getConnectUrl,
  getImportStatus,
  importCalendar,
} from "@/api/client";
import { ImportStatusView, IntegrationView } from "@/api/types";
import { useIntegrations } from "./IntegrationsContext";
import {
  CALENDAR_CAPABILITIES,
  CALENDAR_PROVIDERS,
  CalendarProviderId,
  CalendarProviderMeta,
  availableProviders,
  connectionFor,
  connections,
  describeCallbackError,
  hasIncompleteConsent,
  missingCapabilities,
} from "@/lib/calendarProviders";

/**
 * "Connected calendars" on the profile page, also the landing spot right after first-time
 * setup so connecting is the natural (but optional) last onboarding step.
 *
 * One card per provider this deployment offers. A host connects one calendar at a time, so
 * while one is connected the others' connect buttons are disabled: the connected card already
 * says which calendar is in use, and disconnecting re-enables the rest.
 */
export function CalendarConnectionsPanel() {
  const [busy, setBusy] = React.useState<CalendarProviderId | null>(null);
  const [importing, setImporting] = React.useState(false);
  const [importStatus, setImportStatus] = React.useState<ImportStatusView | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [banner, setBanner] = React.useState<{ tone: "ok" | "error"; text: string } | null>(null);

  // Shared with the rest of the page: connecting or disconnecting here has to move the delivery
  // choice and the setup checklist too, and it cannot do that from a copy only this panel holds.
  const { iv: integrations, refresh, justConnected, connectError } = useIntegrations();

  // The import summary belongs to whatever is connected, so it follows the connections rather than
  // being fetched alongside them.
  React.useEffect(() => {
    if (!integrations || integrations.connections.length === 0) {
      setImportStatus(null);
      return;
    }
    let cancelled = false;
    getImportStatus()
      .then((s) => !cancelled && setImportStatus(s))
      .catch(() => !cancelled && setImportStatus(null));
    return () => {
      cancelled = true;
    };
  }, [integrations]);

  // The consent screen's outcome, read once by the page (see IntegrationsContext) and shown here.
  // The banner names what changed about the host's meetings rather than only that a connection
  // exists: connecting a calendar moves auto-booked meetings onto it, silently, because the stored
  // preference defaults to CALENDAR and merely displayed as email while there was nothing to write
  // to. The switch beside it is the control; this only says what happened.
  React.useEffect(() => {
    if (justConnected) {
      const meta = CALENDAR_PROVIDERS.find((p) => p.id === justConnected.toUpperCase());
      setBanner({
        tone: "ok",
        text: `${meta?.label ?? "Calendar"} connected. Auto-booked meetings now land on it.`,
      });
    } else if (connectError) {
      setBanner({ tone: "error", text: describeCallbackError(connectError) });
    }
  }, [justConnected, connectError]);

  const available = availableProviders(integrations);
  const held = connections(integrations);

  async function onConnect(provider: CalendarProviderMeta) {
    setBusy(provider.id);
    setError(null);
    try {
      const { authorizationUrl } = await getConnectUrl(provider.id);
      window.location.assign(authorizationUrl);
      // No reset: the page is navigating away.
    } catch {
      setError(`Couldn't start the ${provider.label} connection. Try again.`);
      setBusy(null);
    }
  }

  async function onDisconnect(provider: CalendarProviderMeta) {
    setBusy(provider.id);
    setError(null);
    try {
      await disconnectCalendar(provider.id);
      setImportStatus(null);
      refresh();
      // The mirror image of the connect banner: disconnecting moves auto-booked meetings back to
      // email, and saying so is the difference between a setting that changed and one that
      // changed behind the host's back.
      setBanner({
        tone: "ok",
        text: `${provider.label} disconnected. Auto-booked meetings arrive by email again.`,
      });
    } catch {
      setError("Couldn't disconnect. Try again.");
    } finally {
      setBusy(null);
    }
  }

  async function onImport(provider: CalendarProviderMeta) {
    setImporting(true);
    setError(null);
    try {
      const status = await importCalendar(provider.id);
      setImportStatus(status);
      setBanner({
        tone: "ok",
        text: `Imported ${status.count} event${status.count === 1 ? "" : "s"}. They'll show on your calendar.`,
      });
    } catch {
      setError(`Couldn't import your ${provider.label} events. Check the connection and try again.`);
    } finally {
      setImporting(false);
    }
  }

  return (
    <section
      id="calendar-integrations"
      className="scroll-mt-24 rounded-2xl border-l-4 border-brand-coral bg-muted/20 p-4"
    >
      <h2 className="mb-3 flex items-center gap-2 font-comic-headline text-sm uppercase tracking-wide text-muted-foreground">
        <CalendarCheck2 className="h-4 w-4" /> Connected calendars
      </h2>
      <div className="space-y-3">
        {banner && (
          <div
            role={banner.tone === "error" ? "alert" : "status"}
            className={
              banner.tone === "ok"
                ? "rounded-md border border-brand-blue/50 bg-brand-blue/10 px-3 py-2 text-sm text-foreground"
                : "rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-foreground"
            }
          >
            {banner.text}
          </div>
        )}

        {integrations === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          CALENDAR_PROVIDERS.map((provider) => (
            <CalendarProviderCard
              key={provider.id}
              provider={provider}
              available={available.includes(provider.id)}
              connected={connectionFor(integrations, provider.id)}
              blockedBy={held.find((c) => c.provider !== provider.id)?.provider ?? null}
              busy={busy === provider.id}
              importing={importing}
              importStatus={importStatus}
              onConnect={() => onConnect(provider)}
              onDisconnect={() => onDisconnect(provider)}
              onImport={() => onImport(provider)}
            />
          ))
        )}

        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-foreground"
          >
            {error}
          </div>
        )}
      </div>
    </section>
  );
}

type CardProps = {
  provider: CalendarProviderMeta;
  available: boolean;
  connected: IntegrationView | null;
  /** Another provider is holding the one connection slot. */
  blockedBy: string | null;
  busy: boolean;
  importing: boolean;
  importStatus: ImportStatusView | null;
  onConnect: () => void;
  onDisconnect: () => void;
  onImport: () => void;
};

function CalendarProviderCard({
  provider,
  available,
  connected,
  blockedBy,
  busy,
  importing,
  importStatus,
  onConnect,
  onDisconnect,
  onImport,
}: CardProps) {
  const Mark = provider.id === "GOOGLE" ? GoogleMark : MicrosoftMark;
  const missing = missingCapabilities(connected);
  // Gated on ACTIVE: an ERROR connection has to be reconnected regardless, and naming a second
  // problem the host cannot act on separately would only make the first one harder to read.
  const incompleteConsent = connected?.status === "ACTIVE" && hasIncompleteConsent(connected);

  return (
    <div className="rounded-xl border border-border bg-background/60 p-3">
      {/* Stacked for the narrow sidebar: identity, then state, then actions — the email
          truncates rather than pushing past the column. */}
      <div className="flex items-center gap-2">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-background shadow-sm">
          <Mark className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <div className="font-comic-headline text-sm uppercase">{provider.label}</div>
          {connected && (
            <div className="truncate text-sm text-foreground">
              {connected.externalEmail ?? "connected account"}
            </div>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!available ? (
          <span className="rounded-full border border-border bg-muted px-2 py-0.5 font-comic-headline text-[11px] uppercase tracking-wide text-muted-foreground">
            Coming soon
          </span>
        ) : connected ? (
          <>
            {connected.status !== "ACTIVE" ? (
              <span className="rounded-full border border-brand-contrast/40 bg-brand-yellow/30 px-2 py-0.5 font-comic-headline text-[11px] uppercase text-foreground">
                Reconnect needed
              </span>
            ) : incompleteConsent ? (
              /* Same amber as "reconnect needed" because the remedy is the same (go back through
                 consent), but worded for the actual cause: the token is fine, the consent was
                 not. Calling this "connected" is what sent a host looking for the bug in Scheda. */
              <span className="rounded-full border border-brand-contrast/40 bg-brand-yellow/30 px-2 py-0.5 font-comic-headline text-[11px] uppercase text-foreground">
                Permissions missing
              </span>
            ) : (
              <span className="rounded-full border border-border bg-brand-blue px-2 py-0.5 font-comic-headline text-[11px] uppercase text-brand-blue-foreground">
                Connected
              </span>
            )}
            {(connected.status === "ERROR" || incompleteConsent) && (
              <button
                type="button"
                onClick={onConnect}
                disabled={busy}
                className="rounded-full border border-border bg-brand-blue px-3 py-1 font-comic-headline text-[11px] uppercase tracking-wide text-brand-blue-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Reconnect
              </button>
            )}
            <button
              type="button"
              onClick={onDisconnect}
              disabled={busy}
              className="flex items-center gap-1 rounded-full border border-border px-3 py-1 font-comic-headline text-[11px] uppercase tracking-wide transition hover:bg-brand-blue/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Unplug className="h-3.5 w-3.5" />
              <span>{busy ? "…" : "Disconnect"}</span>
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onConnect}
            disabled={busy || blockedBy !== null}
            className="flex items-center gap-2 rounded-full border border-border px-3 py-1.5 font-comic-headline text-[11px] uppercase tracking-wide transition hover:border-brand-blue/50 hover:bg-brand-blue/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Mark className="h-3.5 w-3.5" />
            <span>{busy ? "Opening…" : "Connect"}</span>
          </button>
        )}
      </div>

      {/* Which half is missing, not just that something is. Shown only while something IS missing:
          a full grant needs no commentary, and neither does a connection made before the grant was
          recorded, which reports nothing missing for want of evidence rather than for want of a
          gap. Both permissions are listed so a host who ticked one box can see which one it was. */}
      {incompleteConsent && (
        <ul className="mt-2 space-y-1">
          {CALENDAR_CAPABILITIES.map((capability) => {
            const granted = !missing.includes(capability.id);
            return (
              <li key={capability.id} className="flex items-center gap-1.5 text-sm">
                {granted ? (
                  <Check className="h-3.5 w-3.5 shrink-0 text-brand-teal" aria-hidden />
                ) : (
                  <X className="h-3.5 w-3.5 shrink-0 text-brand-coral" aria-hidden />
                )}
                <span className={granted ? "text-muted-foreground" : "text-foreground"}>
                  {capability.label}
                </span>
                <span className="sr-only">{granted ? " granted" : " not granted"}</span>
              </li>
            );
          })}
        </ul>
      )}

      {connected && (
        <div className="mt-3 space-y-2 border-t border-border pt-3">
          <div className="text-sm text-foreground">
            {importStatus && importStatus.lastImportedAt ? (
              <>
                <span className="font-semibold">{importStatus.count}</span>
                {` event${importStatus.count === 1 ? "" : "s"} on your calendar · ` +
                  relativeTime(importStatus.lastImportedAt)}
              </>
            ) : (
              `Show your ${provider.label} events on the Delgentic calendar (the next month).`
            )}
          </div>
          <button
            type="button"
            onClick={onImport}
            disabled={importing || connected.status !== "ACTIVE"}
            className="flex items-center gap-1 rounded-full border border-border px-3 py-1 font-comic-headline text-[11px] uppercase tracking-wide transition hover:border-brand-teal/50 hover:bg-brand-teal/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <DownloadCloud className="h-3.5 w-3.5" />
            <span>
              {importing
                ? "Importing…"
                : importStatus?.lastImportedAt
                  ? "Re-import"
                  : "Import my events"}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

/** "just now" / "5 min ago" / "3 h ago" / a date, for the last-import line. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Third-party brand marks. These are the documented exception to the tokenised-palette rule,
 * so they stay isolated in their own components rather than leaking literal hues into the UI.
 */
function GoogleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
    </svg>
  );
}

function MicrosoftMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 23 23" className={className} aria-hidden>
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  );
}
